import { apiClient } from './client';

export type TechnologyActionType =
  | 'calibrate_receiver'
  | 'listening_broadcast'
  | 'decode_static'
  | 'calibrate_star_survey'
  | 'analyse_candidate_systems'
  | 'launch_readiness_trial'
  | 'diplomatic_corps_training'
  | 'defence_readiness_drill';

export type MissionType = 'probe' | 'envoy' | 'trade' | 'defence_patrol' | 'raid';
export type RelationActionType =
  | 'peace_offer'
  | 'trade_pact'
  | 'envoy_visit'
  | 'sanctions'
  | 'raid'
  | 'negotiate_detained_envoys'
  | 'reparations'
  | 'defence_commitment';

export interface SpaceMetrics {
  signalClarity: number;
  signalReach: number;
  scannerCalibration: number;
  systemAnalysis: number;
  launchReadiness: number;
  envoyReadiness: number;
  defenceReadiness: number;
}

export interface SpaceCapabilities {
  completedUpgrades: string[];
  radio: boolean;
  scannerTier: number;
  scanCoverage: number;
  detectionConfidence: number;
  reachableRangeLy: number;
  socialUnlocked: boolean;
  spaceExplorationUnlocked: boolean;
  hasSignalArray: boolean;
  hasLaunchComplex: boolean;
  hasSpacePort: boolean;
  hasRocketProgram: boolean;
  hasOrbitalDock: boolean;
  hasDeepSpaceProbe: boolean;
  hasQuantumLab: boolean;
  hasQuantumDrive: boolean;
  hasPlanetaryDefence: boolean;
  hasOrbitalDefence: boolean;
  hasShieldSatellites: boolean;
  spaceMetrics?: SpaceMetrics;
}

export interface SpaceAction {
  id: TechnologyActionType;
  label: string;
  phase: string;
  requirement: string;
  available: boolean;
  lockedReason?: string | null;
  cost: number;
  durationDays: number;
  cooldownDays?: number;
  effects: Record<string, unknown>;
  effectPreview: string;
  description: string;
}

export interface SpaceMission {
  mission_id: string;
  mission_type: string;
  status: string;
  target_planet_id?: string | null;
  target_star_index?: number | null;
  distance_ly: number;
  departure_day: number;
  arrival_day: number;
  outcome?: string | null;
}

export interface SpaceOption {
  id: string;
  label: string;
  available: boolean;
  lockedReason?: string | null;
  cost: number;
  durationDays?: number;
  travelDays?: number;
  cooldownDays?: number;
  effectPreview?: string;
  description: string;
  risk?: string;
}

export interface TargetSpaceOptions {
  signalOptions: SpaceOption[];
  missionOptions: SpaceOption[];
  relationActions: SpaceOption[];
  relation?: {
    relation_id: string;
    planet_a_id: string;
    planet_b_id: string;
    status: string;
    trust: number;
    tension: number;
    trade: number;
    defence: number;
  } | null;
}

export interface SpaceOverview {
  currentDay: number;
  currentBalance: number;
  capabilities: SpaceCapabilities;
  actions: SpaceAction[];
  operationState: {
    active?: {
      operationId: string;
      type: TechnologyActionType;
      label: string;
      startedDay: number;
      durationDays: number;
      remainingDays: number;
      completionDay: number;
      cost: number;
      effectPreview: string;
      description: string;
    } | null;
    history: Array<{
      operationId: string;
      type: TechnologyActionType;
      label: string;
      startedDay: number;
      completedDay: number;
      cost: number;
    }>;
    cooldowns: Record<string, number>;
  };
  spaceProgram: {
    doctrine?: string | null;
    metrics?: Record<string, number>;
    milestones?: Record<string, number>;
  };
  factionGoal?: {
    type: string;
    label: string;
    reason: string;
  };
  score?: {
    score: number;
    faction_goal: string;
    breakdown: Record<string, number>;
  };
  signals: Array<{
    signal_id: string;
    signal_type: string;
    status: string;
    direction_star_index?: number | null;
    detected_planet_id?: string | null;
    sent_day: number;
  }>;
  missions: {
    outbound: SpaceMission[];
    inbound: SpaceMission[];
    completed: SpaceMission[];
    intercepted: SpaceMission[];
    all: SpaceMission[];
  };
  relations: Array<{
    relation_id: string;
    planet_a_id: string;
    planet_b_id: string;
    status: string;
    trust: number;
    tension: number;
    trade: number;
    defence: number;
  }>;
  targetOptions: Record<number, TargetSpaceOptions>;
}

export const spaceApi = {
  getOverview: () => apiClient.get<{ success: boolean; data: SpaceOverview }>('/space'),
  runAction: (actionType: TechnologyActionType) =>
    apiClient.post<{ success: boolean; data: unknown }>('/space/actions', { actionType }),
  broadcastSignal: (input: { signalType?: string; directionStarIndex?: number; message?: string }) =>
    apiClient.post<{ success: boolean; data: unknown }>('/stars/signals', input),
  createMission: (input: { missionType: MissionType; targetPlanetId?: string; targetStarIndex?: number }) =>
    apiClient.post<{ success: boolean; data: unknown }>('/space/missions', input),
  relationAction: (planetId: string, action: RelationActionType) =>
    apiClient.post<{ success: boolean; data: unknown }>(`/relations/${planetId}/action`, { action }),
};
